import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import {
  useExpense, useApproveExpense, useRejectExpense, useDeleteExpense,
} from '@/hooks/useExpenses';
import { ExpenseStatusBadge, SyncStatusBadge } from './ExpenseStatusBadge';
import { CheckCircle2, XCircle, Trash2, Loader2 } from 'lucide-react';

interface Props {
  expenseId: string | null;
  onClose: () => void;
}

export function ExpenseDetailSheet({ expenseId, onClose }: Props) {
  const { t } = useTranslation();
  const exp = useExpense(expenseId);
  const approve = useApproveExpense();
  const reject = useRejectExpense();
  const del = useDeleteExpense();
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  if (!expenseId) return null;
  const e = exp.data;

  const handleApprove = async () => {
    if (!e) return;
    if (!window.confirm(t('expense.approve_confirm', 'Approve this expense? It will be queued for sync to Zoho.'))) return;
    await approve.mutateAsync({ id: e.id });
  };

  const handleReject = async () => {
    if (!e || !rejectReason.trim()) return;
    await reject.mutateAsync({ id: e.id, reason: rejectReason });
    setShowRejectInput(false);
    setRejectReason('');
  };

  const handleDelete = async () => {
    if (!e) return;
    if (!window.confirm(t('expense.delete_confirm', 'Delete this expense? This cannot be undone.'))) return;
    await del.mutateAsync(e.id);
    onClose();
  };

  return (
    <Sheet open={!!expenseId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{exp.isLoading ? t('loading', 'Loading...') : (e?.code ?? t('expense.title', 'Expense'))}</SheetTitle>
        </SheetHeader>

        {exp.isLoading && <Loader2 className="h-5 w-5 animate-spin mt-4" />}

        {e && (
          <div className="space-y-5 py-4">
            <div className="flex flex-wrap gap-2">
              <ExpenseStatusBadge status={e.status} />
              <SyncStatusBadge status={e.sync_status} />
              {e.is_reconciled && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-emerald-50 text-emerald-700">
                  {t('expense.reconciled', '✓ Reconciled')}
                </span>
              )}
            </div>

            {/* Header amount */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-3xl font-semibold">
                {Number(e.amount_sar).toLocaleString()} SAR
              </div>
              {e.vat_amount_sar && Number(e.vat_amount_sar) > 0 && (
                <div className="text-sm text-muted-foreground mt-1">
                  + {Number(e.vat_amount_sar).toLocaleString()} VAT
                </div>
              )}
              <div className="text-xs text-muted-foreground mt-2">
                {new Date(e.expense_date).toLocaleDateString()} · {e.source.replace('_', ' ')}
              </div>
            </div>

            {/* Details grid */}
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {e.category_name_en && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.category', 'Category')}</dt>
                  <dd className="font-medium">{e.category_code} · {e.category_name_en}</dd>
                </div>
              )}
              {e.vendor_name_raw && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.vendor', 'Vendor')}</dt>
                  <dd>{e.vendor_name_raw}</dd>
                </div>
              )}
              {e.invoice_number && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.invoice_number', 'Invoice #')}</dt>
                  <dd>{e.invoice_number}</dd>
                </div>
              )}
              {e.legal_entity_code && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.legal_entity', 'Entity')}</dt>
                  <dd>{e.legal_entity_code}</dd>
                </div>
              )}
              {e.vehicle_plate && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.vehicle', 'Vehicle')}</dt>
                  <dd>{e.vehicle_plate}</dd>
                </div>
              )}
              {(e as any).trip_driver_name && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.driver', 'Driver')}</dt>
                  <dd>{(e as any).trip_driver_name}</dd>
                </div>
              )}
              {e.site_name && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.site', 'Site')}</dt>
                  <dd>{e.site_name}</dd>
                </div>
              )}
              {e.trip_code && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.trip', 'Trip')}</dt>
                  <dd>{e.trip_code}</dd>
                </div>
              )}
              {e.zoho_record_id && (
                <div>
                  <dt className="text-muted-foreground text-xs">{t('expense.zoho_id', 'Zoho ID')}</dt>
                  <dd className="font-mono text-xs">{e.zoho_record_id}</dd>
                </div>
              )}
            </dl>

            {e.description && (
              <div>
                <h4 className="text-xs text-muted-foreground mb-1">{t('expense.description', 'Description')}</h4>
                <p className="text-sm">{e.description}</p>
              </div>
            )}

            {e.notes && (
              <div>
                <h4 className="text-xs text-muted-foreground mb-1">{t('expense.notes', 'Notes')}</h4>
                <p className="text-sm whitespace-pre-line">{e.notes}</p>
              </div>
            )}

            {e.rejection_reason && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <strong>{t('expense.rejected_label', 'Rejected:')}</strong> {e.rejection_reason}
              </div>
            )}

            {/* Actions */}
            {e.status === 'pending_approval' && (
              <div className="space-y-2 pt-2 border-t border-border">
                <h4 className="text-sm font-medium">{t('expense.actions', 'Actions')}</h4>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleApprove} disabled={approve.isPending} variant="gold" size="sm">
                    {approve.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    {t('expense.approve', 'Approve')}
                  </Button>
                  <Button
                    onClick={() => setShowRejectInput(!showRejectInput)}
                    variant="outline"
                    size="sm"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    {t('expense.reject', 'Reject')}
                  </Button>
                  <Button
                    onClick={handleDelete}
                    variant="ghost"
                    size="sm"
                    disabled={del.isPending}
                  >
                    {del.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    {t('delete', 'Delete')}
                  </Button>
                </div>

                {showRejectInput && (
                  <div className="space-y-2 mt-2">
                    <textarea
                      placeholder={t('expense.reject_reason_placeholder', 'Reason for rejection...')}
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                    />
                    <Button
                      onClick={handleReject}
                      disabled={!rejectReason.trim() || reject.isPending}
                      variant="destructive"
                      size="sm"
                    >
                      {reject.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      {t('expense.confirm_reject', 'Confirm rejection')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
